/**
 * Núcleo do "instalar como app" (PWA).
 *
 * O Chrome/Edge/Android disparam o evento `beforeinstallprompt` uma única vez e
 * cedo — muitas vezes antes do React montar. Por isso capturamos o evento no
 * escopo do módulo (importado em `main.tsx`) e o guardamos, expondo um pequeno
 * store observável para a UI acionar a instalação quando o usuário clicar.
 *
 * No iOS/Safari o evento não existe: a instalação é manual (Compartilhar →
 * "Adicionar à Tela de Início"), então mostramos instruções em vez do prompt.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export interface InstallState {
  /** O navegador guardou um prompt nativo pronto para acionar. */
  canPrompt: boolean;
  /** O app já está rodando instalado (standalone). */
  installed: boolean;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

/** Snapshot estável (mesma referência até algo mudar) — exigido pelo useSyncExternalStore. */
let snapshot: InstallState = {
  canPrompt: false,
  installed: isRunningStandalone(),
};

function refresh() {
  snapshot = {
    canPrompt: deferredPrompt !== null,
    installed: isRunningStandalone() || snapshot.installed,
  };
  listeners.forEach((l) => l());
}

/** True quando o app está aberto na janela instalada (sem chrome do navegador). */
export function isRunningStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari expõe isso quando adicionado à tela inicial
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

/** Heurística para iOS/iPadOS no Safari, onde não há `beforeinstallprompt`. */
export function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const iOSDevice = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ se apresenta como "MacIntel" com toque
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  const isSafari = /^((?!chrome|android|crios|fxios|edgios).)*safari/i.test(ua);
  return (iOSDevice || iPadOS) && isSafari;
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Impede o mini-infobar padrão do Chrome; nós mesmos convidamos via UI.
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    refresh();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    snapshot = { ...snapshot, canPrompt: false, installed: true };
    listeners.forEach((l) => l());
  });
}

export function getInstallState(): InstallState {
  return snapshot;
}

export function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Aciona o prompt nativo. Deve partir de um gesto do usuário (clique). */
export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  if (!deferredPrompt) return "unavailable";
  await deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  // O prompt nativo só pode ser usado uma vez.
  deferredPrompt = null;
  refresh();
  return outcome;
}
